import React, { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Form, Input, Label, Select } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import type { ConfigSetting, Product } from '@/features/products/types/products.types';
import { productKeys } from '@/features/products/hooks/useProductsQuery';
import { smriqContextKeys } from '@/features/service-plans/hooks/useSmriqContext';
import { saveSmriqPlanDefaults } from '../services/smriqPlanConfig';
import { useSmriqDurationDefaults } from '../hooks/useSmriqDurationDefaults';
import { makeSmriqPlanDefaultsSchema } from '../schemas/smriqPlanDefaults.schema';
import {
    buildSmriqPlanDefaults,
    readSmriqPlanDefaults,
    type SettingValue,
    type SmriqPlanDefaultsForm,
} from '../utils/smriqPlanDefaults';

interface Props {
    productId: string;
    settings: ConfigSetting[];
}

const MODE_OPTIONS = [
    { value: 'defaults', label: 'Use Defaults' },
    { value: 'fixed', label: 'Fixed' },
    { value: 'type', label: 'Typed Range' },
    { value: 'dropdown', label: 'Dropdown Values' },
];

const PRICING_OPTIONS = [
    { value: 'oem_approved', label: 'OEM approved pricing' },
    { value: 'division_approved', label: 'Division approved pricing (uses OEM pricing when unavailable)' },
];

const MOT_OPTIONS = [
    { value: 'unset', label: 'Not set (dealer must choose)' },
    { value: 'yes', label: 'Include MOT tests' },
    { value: 'no', label: 'Do not include MOT tests' },
];

export const SmriqPlanDefaultsPanel: React.FC<Props> = ({ productId, settings }) => {
    const { data: platformDefaults } = useSmriqDurationDefaults();
    const queryClient = useQueryClient();
    const [saved, setSaved] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [cacheWarning, setCacheWarning] = useState<string | null>(null);

    const resolver = useMemo(
        () => zodResolver(makeSmriqPlanDefaultsSchema(platformDefaults)),
        [platformDefaults],
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm<SmriqPlanDefaultsForm>({
        mode: 'onBlur',
        resolver,
        defaultValues: readSmriqPlanDefaults(settings),
    });

    const durationMode = useWatch({ control, name: 'duration_mode' });

    const saveMutation = useMutation({
        mutationFn: (defaults: Record<string, SettingValue>) => saveSmriqPlanDefaults(productId, defaults),
    });

    // Two caches: the configurator keys the SMRiQ context separately from the product detail.
    // throwOnError is required, or query-core swallows a failed refetch via .catch(noop).
    const invalidateProductViews = () =>
        Promise.all([
            queryClient.invalidateQueries(
                { queryKey: productKeys.detail(productId) },
                { throwOnError: true },
            ),
            queryClient.invalidateQueries(
                { queryKey: smriqContextKeys.detail(productId) },
                { throwOnError: true },
            ),
        ]);

    // A rejected request does not prove the transaction did not commit, so recovery
    // re-reads the server instead of trusting the settings this panel was given.
    const refetchPersistedSettings = async (): Promise<ConfigSetting[] | null> => {
        try {
            await queryClient.refetchQueries(
                { queryKey: productKeys.detail(productId) },
                { throwOnError: true },
            );
            await queryClient.invalidateQueries({ queryKey: smriqContextKeys.detail(productId) });
        } catch {
            return null;
        }

        return queryClient.getQueryData<Product>(productKeys.detail(productId))?.config_settings ?? null;
    };

    const recoverFromFailedSave = async (error: unknown) => {
        const reason = isApiError(error) ? error.message : 'Failed to save SMRiQ plan defaults.';
        const persisted = await refetchPersistedSettings();

        if (persisted === null) {
            setFormError(
                `${reason} The saved configuration could not be re-read, so whether this change was applied is unknown - reload the product before editing again.`,
            );
            return;
        }

        reset(readSmriqPlanDefaults(persisted));
        setFormError(`${reason} The fields below now show the configuration currently saved.`);
    };

    const onSubmit = async (data: SmriqPlanDefaultsForm) => {
        setSaved(false);
        setFormError(null);
        setCacheWarning(null);
        const desired = buildSmriqPlanDefaults(data);
        const payload = Object.fromEntries(
            Object.entries(desired).filter((entry): entry is [string, SettingValue] => entry[1] !== undefined),
        );

        try {
            await saveMutation.mutateAsync(payload);
        } catch (error) {
            await recoverFromFailedSave(error);
            return;
        }

        setSaved(true);

        // Refreshing the caches happens after the write is already acknowledged, so a
        // failure here is a staleness warning and never reported as a failed save.
        try {
            await invalidateProductViews();
        } catch {
            setCacheWarning(
                'The product and quote journey views could not be refreshed. Reload to see the saved configuration.',
            );
        }
    };

    const numberField = (
        name: 'duration_default' | 'duration_min' | 'duration_max',
        id: string,
        label: string,
    ) => (
        <div>
            <Label htmlFor={id}>{label}</Label>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <Input
                        id={id}
                        type="number"
                        inputMode="numeric"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={!!error}
                        hint={error?.message}
                        aria-invalid={!!error}
                    />
                )}
            />
        </div>
    );

    return (
        <div className="space-y-4 max-w-2xl">
            <div>
                <h3 className="font-semibold">Plan defaults</h3>
                <p className="text-sm text-mp-muted">
                    How the SMRiQ plan controls offer a duration, the MOT default, and the pricing used.
                </p>
            </div>

            <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {formError && (
                        <Alert variant="error">{formError}</Alert>
                    )}
                    {saved && (
                        <Alert variant="success" role="status">
                            SMRiQ plan defaults saved.
                        </Alert>
                    )}
                    {cacheWarning && (
                        <Alert variant="warning" role="status">{cacheWarning}</Alert>
                    )}

                    <div>
                        <Label htmlFor="smriq_cfg_duration_mode">Duration</Label>
                        <Controller
                            name="duration_mode"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id="smriq_cfg_duration_mode"
                                    ariaLabel="Duration"
                                    options={MODE_OPTIONS}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            )}
                        />
                    </div>

                    {durationMode === 'defaults' && (
                        <p className="text-sm text-mp-muted">
                            The dealer types a duration within the platform range
                            {platformDefaults
                                ? ` (minimum ${platformDefaults.min} months, maximum ${platformDefaults.max} months)`
                                : ''}
                            .
                        </p>
                    )}

                    {durationMode === 'type' && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {numberField('duration_default', 'smriq_cfg_duration_default', 'Default (months)')}
                            {numberField('duration_min', 'smriq_cfg_duration_min', 'Minimum (months)')}
                            {numberField('duration_max', 'smriq_cfg_duration_max', 'Maximum (months)')}
                        </div>
                    )}

                    {durationMode === 'fixed' && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {numberField(
                                'duration_default',
                                'smriq_cfg_duration_default',
                                'Fixed duration (months)',
                            )}
                        </div>
                    )}

                    {durationMode === 'dropdown' && (
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="smriq_cfg_duration_options">Options (months)</Label>
                                <Controller
                                    name="duration_options"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <Input
                                            id="smriq_cfg_duration_options"
                                            type="text"
                                            placeholder="e.g. 12, 18, 24, 36"
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            error={!!error}
                                            hint={error?.message ?? 'Comma-separated months the dealer picks from.'}
                                            aria-invalid={!!error}
                                        />
                                    )}
                                />
                            </div>
                            {numberField('duration_default', 'smriq_cfg_duration_default', 'Default (months)')}
                        </div>
                    )}

                    <div>
                        <Label htmlFor="smriq_cfg_mot_default">Default MOT inclusion</Label>
                        <Controller
                            name="mot_default"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Select
                                    id="smriq_cfg_mot_default"
                                    ariaLabel="Default MOT inclusion"
                                    options={MOT_OPTIONS}
                                    value={field.value === '' ? 'unset' : field.value}
                                    error={!!error}
                                    hint={error?.message}
                                    onChange={(next) => field.onChange(next === 'unset' ? '' : next)}
                                    onBlur={field.onBlur}
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Controller
                            name="allow_mot_selection"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <Checkbox
                                        id="smriq_cfg_allow_mot"
                                        label="Allow the dealer to change MOT inclusion"
                                        ariaLabel="Allow the dealer to change MOT inclusion"
                                        checked={field.value}
                                        onChange={(checked) => field.onChange(checked)}
                                    />
                                    {error && (
                                        <p className="mt-1.5 text-xs text-mp-error">{error.message}</p>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    <div className="flex flex-col">
                        <Label htmlFor="smriq_cfg_pricing_variant">Pricing</Label>
                        <Controller
                            name="pricing_variant"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id="smriq_cfg_pricing_variant"
                                    ariaLabel="Pricing"
                                    options={PRICING_OPTIONS}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            )}
                        />
                    </div>

                    <Button size="sm" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save plan defaults'}
                    </Button>
                </Form>
            </PermissionGuard>
        </div>
    );
};
